import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import DealAlertOptIn from "@/components/DealAlertOptIn";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, SlidersHorizontal, X, Globe2 } from "lucide-react";

export default function Listings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [meta, setMeta] = useState({ property_types: [], title_statuses: [], regions: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: searchParams.get("q") || "",
    property_type: searchParams.get("property_type") || "",
    title_status: searchParams.get("title_status") || "",
    region: searchParams.get("region") || "",
    budget: searchParams.get("budget") || "",
    diaspora_ready: searchParams.get("diaspora_ready") === "true",
  });

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const fetchProps = async (f) => {
    setLoading(true);
    const params = {};
    if (f.q) params.q = f.q;
    if (f.property_type) params.property_type = f.property_type;
    if (f.title_status) params.title_status = f.title_status;
    if (f.region) params.region = f.region;
    if (f.diaspora_ready) params.diaspora_ready = "true";
    if (f.budget) {
      const [min, max] = f.budget.split("-");
      if (min) params.min_price = min;
      if (max) params.max_price = max;
    }
    try {
      const r = await api.get("/properties", { params });
      setProperties(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = {};
      const f = filters;
      if (f.q) params.q = f.q;
      if (f.property_type) params.property_type = f.property_type;
      if (f.title_status) params.title_status = f.title_status;
      if (f.region) params.region = f.region;
      if (f.diaspora_ready) params.diaspora_ready = "true";
      if (f.budget) {
        const [min, max] = f.budget.split("-");
        if (min) params.min_price = min;
        if (max) params.max_price = max;
      }
      try {
        const r = await api.get("/properties", { params });
        if (!cancelled) setProperties(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === true) next.set(k, "true");
      else if (v) next.set(k, v);
    });
    setSearchParams(next);
    fetchProps(filters);
  };

  const clearAll = () => {
    const cleared = { q: "", property_type: "", title_status: "", region: "", budget: "", diaspora_ready: false };
    setFilters(cleared);
    setSearchParams(new URLSearchParams());
    fetchProps(cleared);
  };

  const activeChips = Object.entries(filters).filter(([, v]) => v);

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="listings-page">
      <div className="mb-8">
        <div className="eyebrow mb-2">Marketplace</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Browse property across Uganda</h1>
        <p className="text-muted-foreground mt-2 max-w-xl">Filter by region, type, title status, and price.</p>
      </div>

      <form onSubmit={apply} className="bg-card border border-border rounded-2xl p-4 md:p-5 grid md:grid-cols-12 gap-3 mb-6" data-testid="listings-filters">
        <div className="md:col-span-4 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by location or keyword…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="pl-10 h-11 rounded-full border-border"
            data-testid="filter-q"
          />
        </div>
        <Select value={filters.property_type} onValueChange={(v) => setFilters({ ...filters, property_type: v })}>
          <SelectTrigger className="md:col-span-2 h-11 rounded-full border-border" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>{meta.property_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.title_status} onValueChange={(v) => setFilters({ ...filters, title_status: v })}>
          <SelectTrigger className="md:col-span-2 h-11 rounded-full border-border" data-testid="filter-title"><SelectValue placeholder="Title" /></SelectTrigger>
          <SelectContent>{meta.title_statuses?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.region} onValueChange={(v) => setFilters({ ...filters, region: v })}>
          <SelectTrigger className="md:col-span-1 h-11 rounded-full border-border" data-testid="filter-region"><SelectValue placeholder="Region" /></SelectTrigger>
          <SelectContent>{meta.regions?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.budget} onValueChange={(v) => setFilters({ ...filters, budget: v })}>
          <SelectTrigger className="md:col-span-2 h-11 rounded-full border-border" data-testid="filter-budget"><SelectValue placeholder="Budget" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0-10000000">Under UGX 10M</SelectItem>
            <SelectItem value="10000000-50000000">10M – 50M</SelectItem>
            <SelectItem value="50000000-200000000">50M – 200M</SelectItem>
            <SelectItem value="200000000-99999999999">200M+</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" className="md:col-span-1 h-11 rounded-full bg-primary hover:bg-primary/90" data-testid="filter-apply">Apply</Button>
      </form>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button
          type="button"
          onClick={() => {
            const next = { ...filters, diaspora_ready: !filters.diaspora_ready };
            setFilters(next);
            const params = new URLSearchParams();
            Object.entries(next).forEach(([k, v]) => {
              if (v === true) params.set(k, "true");
              else if (v) params.set(k, v);
            });
            setSearchParams(params);
            fetchProps(next);
          }}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
            filters.diaspora_ready
              ? "bg-primary text-primary-foreground border-primary shadow-sm"
              : "bg-background text-foreground border-border hover:bg-muted/60"
          }`}
          data-testid="diaspora-toggle"
        >
          <Globe2 className="w-4 h-4" /> Diaspora-ready only
        </button>
      </div>

      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
          {activeChips.map(([k, v]) => (
            <span key={k} className="px-3 py-1 bg-muted rounded-full text-xs font-medium">
              {k}: {v}
            </span>
          ))}
          <button onClick={clearAll} className="text-xs text-primary flex items-center gap-1 ml-2" data-testid="filter-clear">
            <X className="w-3 h-3" /> Clear all
          </button>
        </div>
      )}

      <div className="text-sm text-muted-foreground mb-4" data-testid="listings-count">
        {loading ? "Loading…" : `${properties.length} ${properties.length === 1 ? "property" : "properties"}`}
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="aspect-[4/3] bg-muted rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-24 bg-muted/40 rounded-2xl">
          <h3 className="font-display text-2xl">No properties match those filters.</h3>
          <p className="text-muted-foreground mt-2">Try clearing some filters or browse all listings.</p>
          <Button onClick={clearAll} className="mt-6 rounded-full bg-primary hover:bg-primary/90" data-testid="empty-clear-btn">Clear filters</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
          {properties.map((p, i) => <PropertyCard p={p} index={i} key={p.id} />)}
        </div>
      )}

      <div className="mt-16" data-testid="deal-alert-section">
        <DealAlertOptIn />
      </div>
    </main>
  );
}
